import React from "react";
import moment from "moment";
import {Box, ButtonBase, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CallIcon, CheckmarkCircle02Icon, Message02Icon, ViewIcon} from "@hugeicons/core-free-icons";
import {
  AVATAR_COLORS, initialsOf, isAnnotated, SenderName, StatusBadge, TransferNumber
} from "../dashboard/components/conversationUi";
import {useRegistryProject} from "../RegistryProjectContext";

const formatDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

// One conversation in the supervision list. now: shared clock, passed only for active calls (live duration).
export const SupervisionListItem = React.memo(({conversation, name, language, tags, selected, isVoice, now, onClick}) => {
  const {isAnnotator} = useRegistryProject();
  const showAnnotated = isAnnotator && isAnnotated(conversation);
  const initials = initialsOf(name);
  const avatar = AVATAR_COLORS[(Number(conversation.id) || 0) % AVATAR_COLORS.length];
  const isActive = conversation.status === "ACTIVE";
  const isResolved = conversation.resolved && conversation.status !== "SUCCESS";
  const created = moment(conversation.dateCreated);

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: "100%", display: "flex", alignItems: "flex-start", gap: 1.25, textAlign: "left",
        px: 1.5, py: 1.25, borderRadius: "12px", position: "relative",
        bgcolor: selected ? "rgba(132, 145, 247, 0.12)" : "transparent",
        opacity: isResolved && !selected ? 0.7 : 1,
        "&:hover": {bgcolor: selected ? "rgba(132, 145, 247, 0.16)" : "#F6F6FB"},
      }}
    >
      <Box sx={{position: "relative", flexShrink: 0}}>
        <Box sx={{
          width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 600, bgcolor: avatar.bg, color: avatar.color,
        }}>
          {initials || <HugeiconsIcon icon={isVoice ? CallIcon : Message02Icon} size={16}/>}
        </Box>
        {isActive && (
          <Box sx={{
            position: "absolute", right: -1, bottom: -1, width: 11, height: 11, borderRadius: "50%",
            bgcolor: "#22C55E", border: "2px solid #fff",
          }}/>
        )}
      </Box>

      <Box sx={{flex: 1, minWidth: 0}}>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1}>
          <Stack direction={"row"} alignItems={"center"} gap={0.75} sx={{minWidth: 0}}>
            <SenderName name={name} sender={conversation.sender} sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}/>
            <TransferNumber conversation={conversation}/>
          </Stack>
          <Typography sx={{
            fontSize: 11, flexShrink: 0, fontVariantNumeric: "tabular-nums",
            color: isActive ? "#1E8A55" : "#8A8AA0", fontWeight: isActive ? 600 : 400,
          }}>
            {isActive && now
              ? formatDuration(now - created.valueOf())
              : created.isSame(moment(), "day") ? created.format("HH:mm") : created.format("DD MMM")}
          </Typography>
        </Stack>
        <Stack direction={"row"} alignItems={"center"} gap={0.75} sx={{mt: 0.5, minWidth: 0}}>
          <StatusBadge conversation={conversation}/>
          {language && (
            <Tooltip title={language.languageName || ""}>
              <Box component={"span"} sx={{
                px: 0.75, py: 0.125, borderRadius: "6px", fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
                textTransform: "uppercase", color: "#4A4A63", bgcolor: "#F2F2F7", flexShrink: 0,
              }}>
                {language.languageCode || language.languageName}
              </Box>
            </Tooltip>
          )}
          {showAnnotated && (
            <Box component={"span"} sx={{
              px: 0.75, py: 0.125, borderRadius: "6px", fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
              textTransform: "uppercase", color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)", flexShrink: 0,
            }}>
              Annotated
            </Box>
          )}
          <Box sx={{flex: 1}}/>
          {conversation.monitoredBy && (
            <Tooltip title={`Supervised by ${conversation.monitoredBy}`}>
              <Box sx={{display: "flex", color: "#8491F7"}}><HugeiconsIcon icon={ViewIcon} size={14}/></Box>
            </Tooltip>
          )}
          {isResolved && (
            <Tooltip title={"Resolved"}>
              <Box sx={{display: "flex", color: "#1E8A55"}}><HugeiconsIcon icon={CheckmarkCircle02Icon} size={14}/></Box>
            </Tooltip>
          )}
        </Stack>
        <Typography noWrap sx={{mt: 0.5, fontSize: 11, color: "#8A8AA0"}}>
          #{conversation.id}{tags?.length ? ` · ${tags.join(", ")}` : ""}
        </Typography>
      </Box>
    </ButtonBase>
  );
});
